import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Mail, Lock, Eye, EyeOff, AlertCircle, ArrowLeft, CheckCircle } from 'lucide-react';
import { useAuth } from '../App';
import { t } from '../i18n';
import { toast } from 'sonner';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [resetSent, setResetSent] = useState(false);
  const [resetToken, setResetToken] = useState('');
  
  const { login, googleLogin, user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const from = location.state?.from?.pathname || '/dashboard';

  // Redirect if already logged in
  if (user) {
    const target = user.role === 'partner' ? '/partners/dashboard' : from;
    navigate(target, { replace: true });
    return null;
  }

  const formatApiErrorDetail = (detail) => {
    if (detail == null) return "Une erreur s'est produite. Veuillez réessayer.";
    if (typeof detail === "string") return detail;
    if (Array.isArray(detail))
      return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
    if (detail && typeof detail.msg === "string") return detail.msg;
    return String(detail);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const result = await login(email, password);
      toast.success(t('auth.loginBtn') + ' !', { duration: 800 });
      const target = result?.role === 'partner' ? '/partners/dashboard' : from;
      navigate(target, { replace: true });
    } catch (err) {
      const errorMsg = formatApiErrorDetail(err.response?.data?.detail) || err.message;
      setError(errorMsg);
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const response = await axios.post(`${API}/auth/forgot-password`, { email });
      setResetSent(true);
      // In dev mode, the token is returned for testing
      if (response.data.token) {
        setResetToken(response.data.token);
      }
      toast.success(t('auth.resetBtn'));
    } catch (err) {
      const errorMsg = formatApiErrorDetail(err.response?.data?.detail) || err.message;
      setError(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  // Forgot Password View
  if (showForgotPassword) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center px-6 py-12">
        <div className="w-full max-w-md">
          {/* Logo */}
          <Link to="/" className="flex items-center justify-center gap-3 mb-8">
            <div className="bg-primary w-12 h-12 rounded-full border-2 border-charcoal flex items-center justify-center shadow-brutal">
              <span className="font-heading font-bold text-xl text-charcoal">T</span>
            </div>
            <span className="font-heading font-bold text-2xl text-charcoal">Tagora</span>
          </Link>

          <div className="card-brutal">
            <button
              onClick={() => { setShowForgotPassword(false); setResetSent(false); setError(''); }}
              className="flex items-center gap-2 text-charcoal/70 hover:text-charcoal mb-4 transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
              Retour
            </button>

            {resetSent ? (
              <div className="text-center">
                <div className="bg-secondary w-16 h-16 rounded-full border-2 border-charcoal flex items-center justify-center mx-auto mb-4">
                  <CheckCircle className="w-8 h-8 text-charcoal" strokeWidth={2.5} />
                </div>
                <h1 className="text-2xl font-bold text-charcoal font-heading mb-2">Email envoyé!</h1>
                <p className="text-charcoal/70 mb-6">
                  Si un compte existe avec cet email, vous recevrez un lien de réinitialisation.
                </p>
                
                {/* Dev mode: Show reset link */}
                {resetToken && (
                  <div className="bg-accent/30 border-2 border-charcoal rounded-xl p-4 mb-4 text-left">
                    <p className="text-xs font-bold text-charcoal mb-2">Mode développement:</p>
                    <Link
                      to={`/reset-password?token=${resetToken}`}
                      className="text-sm text-primary font-bold hover:underline break-all"
                    >
                      Cliquez ici pour réinitialiser
                    </Link>
                  </div>
                )}
                
                <button
                  onClick={() => { setShowForgotPassword(false); setResetSent(false); }}
                  className="btn-primary"
                >
                  Retour à la connexion
                </button>
              </div>
            ) : (
              <>
                <h1 className="text-2xl font-bold text-charcoal font-heading mb-2">{t('auth.forgotPassword')}</h1>
                <p className="text-charcoal/70 mb-6">
                  {t('auth.resetDesc')} et nous vous enverrons un lien de réinitialisation.
                </p>

                {error && (
                  <div className="bg-danger/10 border-2 border-danger rounded-lg p-4 mb-6 flex items-start gap-3">
                    <AlertCircle className="w-5 h-5 text-danger flex-shrink-0 mt-0.5" />
                    <p className="text-sm text-danger">{error}</p>
                  </div>
                )}

                <form onSubmit={handleForgotPassword} className="space-y-5">
                  <div>
                    <label className="text-xs font-bold tracking-[0.1em] uppercase text-charcoal mb-2 block">
                      {t('auth.email')}
                    </label>
                    <div className="relative">
                      <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-charcoal/40" />
                      <input
                        type="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        data-testid="forgot-email"
                        className="input-brutal w-full pl-12"
                        placeholder={t('auth.email')}
                        required
                      />
                    </div>
                  </div>

                  <button
                    type="submit"
                    disabled={loading}
                    data-testid="forgot-submit-btn"
                    className="w-full btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {loading ? '...' : t('auth.resetBtn')}
                  </button>
                </form>
              </>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Main Login View
  return (
    <div className="min-h-screen bg-background flex items-center justify-center px-6 py-12">
      <div className="w-full max-w-md">
        {/* Logo */}
        <Link to="/" className="flex items-center justify-center gap-3 mb-8">
          <div className="bg-primary w-12 h-12 rounded-full border-2 border-charcoal flex items-center justify-center shadow-brutal">
            <span className="font-heading font-bold text-xl text-charcoal">T</span>
          </div>
          <span className="font-heading font-bold text-2xl text-charcoal">Tagora</span>
        </Link>

        {/* Card */}
        <div className="card-brutal">
          <h1 className="text-2xl font-bold text-charcoal font-heading mb-2">{t('auth.loginTitle')}</h1>
          <p className="text-charcoal/70 mb-8">{t('auth.loginSubtitle')}</p>

          {/* Google Login */}
          <button
            type="button"
            onClick={() => googleLogin(from)}
            data-testid="google-login-btn"
            className="w-full btn-outline flex items-center justify-center gap-3 mb-6"
          >
            <svg className="w-5 h-5" viewBox="0 0 24 24">
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
              <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
            </svg>
            {t('auth.orGoogle')}
          </button>

          <div className="relative my-6">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t-2 border-charcoal/20"></div>
            </div>
            <div className="relative flex justify-center">
              <span className="bg-white px-4 text-sm text-charcoal/50">{t('auth.or')}</span>
            </div>
          </div>

          {/* Error Message */}
          {error && (
            <div className="bg-danger/10 border-2 border-danger rounded-lg p-4 mb-6 flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-danger flex-shrink-0 mt-0.5" />
              <p className="text-sm text-danger">{error}</p>
            </div>
          )}

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label className="text-xs font-bold tracking-[0.1em] uppercase text-charcoal mb-2 block">
                {t('auth.email')}
              </label>
              <div className="relative">
                <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-charcoal/40" />
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  data-testid="login-email"
                  className="input-brutal w-full pl-12"
                  placeholder={t('auth.email')}
                  required
                />
              </div>
            </div>

            <div>
              <div className="flex justify-between items-center mb-2">
                <label className="text-xs font-bold tracking-[0.1em] uppercase text-charcoal">
                  {t('auth.password')}
                </label>
                <button
                  type="button"
                  onClick={() => setShowForgotPassword(true)}
                  data-testid="forgot-password-link"
                  className="text-xs text-primary font-bold hover:underline"
                >
                  {t('auth.forgotPassword')}
                </button>
              </div>
              <div className="relative">
                <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-charcoal/40" />
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  data-testid="login-password"
                  className="input-brutal w-full pl-12 pr-12"
                  placeholder="••••••••"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-charcoal/40 hover:text-charcoal"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              data-testid="login-submit-btn"
              className="w-full btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? t('auth.loginBtn') + '...' : t('auth.loginBtn')}
            </button>
          </form>

          <p className="text-center mt-6 text-charcoal/70">
            {t('auth.noAccount')}{' '}
            <Link to="/register" className="text-primary font-bold hover:underline" data-testid="register-link">
              {t('nav.register')}
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default Login;
